import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The read for the Enforcement screen (task 11.8; design 11, 12.2 Enforcement row).
 *
 * ONE operator-API operation backs the screen, role `viewer`, taken straight from the generated
 * `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/enforcement/summary?from=…&to=…` → {@link EnforcementSummary}: Resource Server
 *   verification outcomes by path and error code, the replay-by-namespace breakdown, and the
 *   Gateway stage/code failure breakdown. By contract the two breakdowns are ALWAYS present and
 *   zero-filled — `replay_by_namespace` has one entry per `enum_replay_namespace` catalog value and
 *   `gateway_stage_failures` has one entry per `enum_gateway_stage`, each carrying every
 *   `enum_gateway_error_code` — so an operator always sees a complete grid, with explicit zeros
 *   when nothing fired (FR-5.2, FR-5.3). The screen renders those zero rows as real rows; it never
 *   drops them (that is the whole point of the always-present guarantee).
 *
 * The window is resolved from the shared time-range exactly as the Overview/Decisions/Identity
 * screens do, so pausing freezes the query: in live mode "now" is bucketed to a 10s grid to keep
 * the query key stable between ticks; when frozen (paused or custom) `resolveWindow` returns the
 * exact frozen window and the key stops advancing (design 12.2, FR-13 AC2). The summary is a REST
 * snapshot (not a live-stream view) because it is an aggregate over the whole window — the live
 * stream carries individual deltas, not the catalog-complete breakdowns this screen needs. Fetches
 * go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and the shell
 * shows "Waking the control plane"; queries are retried per `query-client.ts`.
 */

/** The contract `EnforcementSummary` response body. */
export type EnforcementSummary = components["schemas"]["EnforcementSummary"];
/** A single Resource Server verification row (path + code + count). */
export type ResourceVerificationRow = EnforcementSummary["resource_verification"][number];
/** A single replay-by-namespace row (namespace + count). */
export type ReplayRow = EnforcementSummary["replay_by_namespace"][number];
/** A single Gateway stage row (stage + total + per-code counts). */
export type GatewayStageRow = EnforcementSummary["gateway_stage_failures"][number];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface EnforcementWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseEnforcementSummaryResult {
  readonly query: UseQueryResult<EnforcementSummary, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: EnforcementWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): EnforcementWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the enforcement summary for the current window. The query key carries the window so a
 * frozen window never refetches and a live window advances on the 10s grid; `staleTime` /
 * `refetchInterval` match the other screens (live tolerates a little staleness, frozen never goes
 * stale).
 */
export function useEnforcementSummary(): UseEnforcementSummaryResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<EnforcementSummary, ApiError>({
    queryKey: ["enforcement-summary", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<EnforcementSummary>("/enforcement/summary", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
